<script>
  import { untrack } from 'svelte'
  import { useForm } from '@inertiajs/svelte'
  import { liveValidate } from '../../lib/live_validation.svelte.js'
  import { loadSelectOptions } from '../../lib/shared_props.js'
  import Drawer from '../drawer.svelte'
  import MultiSelect from '../multi_select.svelte'
  import ImageSelector from '../image_selector.svelte'
  import TextField from '../form/text_field.svelte'
  import TextareaField from '../form/textarea_field.svelte'
  import SelectField from '../form/select_field.svelte'
  import CheckboxField from '../form/checkbox_field.svelte'
  import SubmitButton from '../form/submit_button.svelte'

  // The campaign form's "Create New Adventure" (old CMS Adventures step drawer): creates a
  // deal or event without leaving the half-filled campaign, starting from its town and place.
  // Store-item config, scheduling and the rest live on the adventure's own page.
  // Posts to the real create action (AdventuresController#quick_create_response): the server
  // fills the reward description / place categories, validates live (Precognition,
  // `adventure.*` keys), and answers with the adventure as flash data (the picker's option
  // shape) or its errors in the `quick_adventure` error bag. `options` are server-built.
  let { open = $bindable(false), options = {}, areaId = null, placeId = null, oncreated } = $props()

  const blank = () => ({
    name: '',
    adventure_type: 'reward',
    area_id: '',
    place_id: '',
    point_source_id: '',
    sub_category_ids: [],
    title: '',
    description: '',
    savings: '',
    spend: '',
    redemption_policy: 'once_per_day',
    requires_location: true,
    image_ids: []
  })

  const form = useForm({ quick_create: true, adventure: blank() }).withPrecognition('post', '/adventures')
  const live = liveValidate(form, 'adventure')

  let images = $state([])
  $effect(() => {
    $form.adventure.image_ids = images.map((image) => image.id)
  })

  $effect(() => {
    if (!open) return
    untrack(() => {
      $form.adventure = { ...blank(), area_id: areaId ?? '', place_id: placeId ?? '' }
      images = []
      $form.clearErrors()
      live.reset()
    })
  })

  const error = (key) => $form.errors[`adventure.${key}`]

  const submit = (event) => {
    event.preventDefault()
    $form.post('/adventures', {
      preserveState: true,
      preserveScroll: true,
      only: ['flash'],
      errorBag: 'quick_adventure',
      // The flash carries only the id; the picker's option shape (images, savings, …) comes
      // from the same lookup a saved value's label does.
      onFlash: async ({ created_adventure: createdAdventure }) => {
        if (!createdAdventure) return
        open = false
        const { options } = await loadSelectOptions('adventures', { ids: [createdAdventure.id] })
        if (options[0]) oncreated?.(options[0])
      }
    })
  }
</script>

<Drawer bind:open title="Create New Adventure" width="w-[40rem]">
  <form novalidate onsubmit={submit} class="space-y-4">
    <TextField
      bind:value={$form.adventure.name}
      label="Name"
      name="quick_adventure_name"
      help="This value is only for internal use and is not displayed in the app."
      error={error('name')}
    />
    <SelectField bind:value={$form.adventure.adventure_type} label="Type" name="quick_adventure_adventure_type" options={options.adventure_types} error={error('adventure_type')} />
    <SelectField bind:value={$form.adventure.area_id} label="College Town" name="quick_adventure_area_id" options={options.areas} error={error('area_id')} />
    <SelectField
      bind:value={$form.adventure.place_id}
      label="Place"
      name="quick_adventure_place_id"
      source="places"
      sourceParams={{ area_id: $form.adventure.area_id }}
      error={error('place_id')}
    />
    {#if $form.adventure.adventure_type === 'reward'}
      <SelectField
        bind:value={$form.adventure.point_source_id}
        label="Point Source"
        name="quick_adventure_point_source_id"
        options={options.point_sources}
        placeholder="Select a point source…"
        error={error('point_source_id')}
      />
    {/if}
    <MultiSelect bind:value={$form.adventure.sub_category_ids} options={options.sub_categories} label="Categories" placeholder="Select a category…" help="Defaults to the place's categories." />

    <TextField bind:value={$form.adventure.title} label="Title" name="quick_adventure_title" error={error('title')} />
    <TextareaField bind:value={$form.adventure.description} label="Description" name="quick_adventure_description" error={error('description')} />
    <TextField bind:value={$form.adventure.savings} label="Estimated Savings" name="quick_adventure_savings" placeholder="$10" error={error('savings')} />
    <TextField bind:value={$form.adventure.spend} label="Estimated Spend" name="quick_adventure_spend" placeholder="$20" error={error('spend')} />
    <SelectField
      bind:value={$form.adventure.redemption_policy}
      label="Redemption Policy"
      name="quick_adventure_redemption_policy"
      options={options.redemption_policies}
      error={error('redemption_policy')}
    />
    <CheckboxField bind:checked={$form.adventure.requires_location} label="Require Geofence Location" name="quick_adventure_requires_location" error={error('requires_location')} />
    <ImageSelector bind:value={images} label="Image(s)" placeId={$form.adventure.place_id} />

    {#if error('base')}<p class="text-sm text-error">{error('base')}</p>{/if}

    <SubmitButton processing={$form.processing} label="Create adventure" />
  </form>
</Drawer>
